import { useEffect, useRef, useState } from 'react';

export type ScrollDirection = 'up' | 'down' | null;

export interface ScrollState {
  isScrolled: boolean;
  direction: ScrollDirection;
}

export interface UseScrollOptions {
  /**
   * scroll-end 디바운스 시간(ms). 마지막 scroll 이벤트 후 이 시간 동안 추가 이벤트가 없으면
   * isScrolling 이 false 가 된다. 기본 150ms.
   */
  scrollEndDelayMs?: number;
}

export interface UseScrollResult extends ScrollState {
  /**
   * scroll 이벤트가 막 발생했거나 발생 중인지. scrollEndDelayMs 동안 추가 이벤트가
   * 없으면 false 로 settle 된다. spacer 처럼 스크롤 도중 freeze 가 필요한 호스트가 사용.
   */
  isScrolling: boolean;
}

const DEFAULT_SCROLL_END_DELAY_MS = 150;

/**
 * isScrolled boundary 의 1px 진동을 흡수하는 dead zone (px).
 *
 * `currentY > threshold` 단일 비교는 boundary 근처에서 1px 진동에도 isScrolled 가 토글되는
 * 약점이 있다. boundary 가 사용자가 자주 머무는 위치에 오면 trackpad 미세 떨림만으로도
 * isScrolled true↔false 가 진동하고, useScroll 이 이때 direction 을 null 로 강제 리셋하므로
 * reveal Bottom 의 펼침/접힘 진동이 유발된다.
 *
 * scrolled 상태에서 false 로 복귀하려면 `threshold - HYSTERESIS` 아래로 더 내려가야 하도록
 * 비대칭 boundary 를 둬서 진동을 흡수한다.
 */
const SCROLLED_HYSTERESIS_PX = 8;

/**
 * direction 의 'up' ↔ 'down' 토글에 적용하는 시간 cooldown (ms).
 *
 * `Header.Bottom` 의 collapse/reveal 동작이 wrapper.offsetHeight 를 변동시키면 브라우저가
 * scrollY 를 자동 보정하는 경우가 있어 direction 이 자체 토글되는 cycle (collapse → 보정 →
 * 'up' 토글 → reveal → 보정 → 'down' 토글 → ...) 이 발생할 수 있다. 마지막 toggle 후 N ms
 * 동안 새 toggle 을 차단해 cycle 진입을 막는다.
 *
 * 첫 toggle 은 즉시 발동하며 cooldown 은 다음 toggle 만 막는다 — 의도적 방향 변경의 반응성은
 * 유지되고, 빠른 진동만 차단된다. null ↔ direction 전환 (스크롤 시작/정지) 에는 cooldown 미적용.
 */
const DIRECTION_COOLDOWN_MS = 500;

/**
 * 스크롤 threshold 초과 여부 + 방향 + 진행 중 여부를 통합 감지.
 *
 * ## 출력
 * - `isScrolled`: scrollY 가 threshold 를 초과한 상태
 * - `direction`: `'up' | 'down' | null`. threshold 이하면 항상 null
 * - `isScrolling`: scroll 이벤트가 진행 중 (scrollEndDelayMs 동안 무이벤트면 false)
 *
 * ## 이벤트 흐름 (한 scroll 이벤트당)
 * 1. window scroll 이벤트 → `isScrolling=true` 즉시 토글 + scrollEnd 타이머 (re)set
 * 2. rAF throttle → 한 frame 당 한 번만 평가
 * 3. 현재 scrollY 를 입력으로 nextScrolled, nextDirection 결정 (아래 hysteresis 3단계 적용)
 * 4. 이전 commit 값과 다르면 `setState` + `onScrollChange` 호출
 *
 * ## 떨림 보호 — 직렬로 적용되는 3단계 hysteresis
 *
 * 단순 비교(`currentY > threshold`)만 쓰면 boundary 1px 진동·작은 반등·scrollY 자동 보정 등으로
 * `isScrolled` / `direction` 이 짧은 시간에 토글되어 호출자(예: Header.Bottom collapse/reveal)에
 * 떨림으로 전파된다. 아래 3 단계가 서로 다른 진동 원인을 막는다.
 *
 * **(1) boundary hysteresis — `isScrolled` 의 비대칭 토글**
 * - false → true: `currentY > threshold`
 * - true → false: `currentY > threshold - SCROLLED_HYSTERESIS_PX`
 * - **잡는 진동**: boundary 근처에서 trackpad 미세 떨림에 의한 isScrolled true↔false 1px 진동
 *
 * **(2) anchor-based distance hysteresis — `direction` 의 거리 기반 토글**
 * - 'down' 유지 중: anchorY = 최고점 추적 (`currentY > anchorY` 이면 갱신)
 * - 'down' → 'up' 전환: `anchorY - currentY > threshold` (사용자가 충분히 위로 이동)
 * - 'up' 유지 중: anchorY = 최저점 추적
 * - 'up' → 'down' 전환: `currentY - anchorY > threshold` (anchor 갱신)
 * - **잡는 진동**: 작은 반등 (예: trackpad momentum 의 짧은 역방향) 으로 direction 이 토글되는 것
 *
 * **(3) direction cooldown — `direction` 의 시간 기반 토글 제한**
 * - 마지막 'up'↔'down' 토글 후 `DIRECTION_COOLDOWN_MS` 동안 새 토글 차단 (차단 시 anchorY 갱신도 보류)
 * - **잡는 진동**: collapse/reveal 로 wrapper.offsetHeight 가 변동 → 브라우저가 scrollY 자동 보정 →
 *   useScroll 이 그 보정을 방향 전환으로 인식 → direction 토글 → wrapper 재변동 → ... 의 cycle
 * - 첫 토글은 즉시 발동 (`lastDirectionToggleAt = -Infinity` 로 시작)
 *
 * **`null` ↔ direction 전환** (스크롤 진입/이탈)
 * - `isScrolled=false` 가 되면 → `direction=null`, anchorY 도 currentY 로 리셋
 * - null → direction (스크롤 첫 진입): 첫 scrollY 증감 방향. cooldown 미적용 (즉시 반응성)
 *
 * ## 기타
 * - rAF throttle 로 frame 당 한 번만 평가
 * - SSR 안전 — `useEffect` 내에서만 `window` 접근, 서버 렌더 시 초기 state 만 반환
 */
export const useScroll = (
  threshold: number,
  onScrollChange?: (state: ScrollState) => void,
  options?: UseScrollOptions,
): UseScrollResult => {
  const [state, setState] = useState<ScrollState>({ isScrolled: false, direction: null });
  const [isScrolling, setIsScrolling] = useState(false);
  const rafRef = useRef(0);
  const scrollEndTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onScrollChangeRef = useRef(onScrollChange);
  onScrollChangeRef.current = onScrollChange;

  const scrollEndDelayMs = options?.scrollEndDelayMs ?? DEFAULT_SCROLL_END_DELAY_MS;
  const scrollEndDelayRef = useRef(scrollEndDelayMs);
  scrollEndDelayRef.current = scrollEndDelayMs;

  useEffect(() => {
    // scrolled 상태에서 false 로 복귀할 때 사용하는 비대칭 boundary.
    // threshold 가 hysteresis 보다 작아도 음수로 가지 않도록 0 으로 clamp.
    const exitThreshold = Math.max(0, threshold - SCROLLED_HYSTERESIS_PX);
    let prevY = window.scrollY;
    let anchorY = prevY;
    let prevScrolled = prevY > threshold;
    let prevDirection: ScrollDirection = prevScrolled ? 'down' : null;
    // direction 의 'up' ↔ 'down' 토글 시간 추적 (cooldown 용).
    // -Infinity 로 시작해 첫 toggle 은 cooldown 무관하게 즉시 발동.
    let lastDirectionToggleAt = Number.NEGATIVE_INFINITY;

    const initial: ScrollState = { isScrolled: prevScrolled, direction: prevDirection };
    setState((prev) => {
      if (prev.isScrolled === initial.isScrolled && prev.direction === initial.direction) {
        return prev;
      }
      return initial;
    });
    onScrollChangeRef.current?.(initial);

    const handleScroll = () => {
      // isScrolling 게이트: 이벤트 발생 시 즉시 true, debounce 로 false 복귀.
      // setState 는 false → true 전환 시에만 호출 (이미 true 면 noop).
      setIsScrolling((prev) => (prev ? prev : true));
      if (scrollEndTimerRef.current !== null) {
        clearTimeout(scrollEndTimerRef.current);
      }
      scrollEndTimerRef.current = setTimeout(() => {
        scrollEndTimerRef.current = null;
        setIsScrolling(false);
      }, scrollEndDelayRef.current);

      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const currentY = window.scrollY;
        // 비대칭 boundary: true→false 복귀 시 더 깊은 임계점(exitThreshold) 사용.
        const nextScrolled = prevScrolled ? currentY > exitThreshold : currentY > threshold;

        let nextDirection: ScrollDirection;
        if (!nextScrolled) {
          nextDirection = null;
          anchorY = currentY;
        } else if (prevDirection === 'down') {
          if (currentY > anchorY) anchorY = currentY;
          nextDirection = anchorY - currentY > threshold ? 'up' : 'down';
          if (nextDirection === 'up') {
            // cooldown 안이면 toggle 차단 — anchorY 도 갱신하지 않음.
            const now = performance.now();
            if (now - lastDirectionToggleAt < DIRECTION_COOLDOWN_MS) {
              nextDirection = 'down';
            } else {
              lastDirectionToggleAt = now;
              anchorY = currentY;
            }
          }
        } else if (prevDirection === 'up') {
          if (currentY < anchorY) anchorY = currentY;
          nextDirection = currentY - anchorY > threshold ? 'down' : 'up';
          if (nextDirection === 'down') {
            const now = performance.now();
            if (now - lastDirectionToggleAt < DIRECTION_COOLDOWN_MS) {
              nextDirection = 'up';
            } else {
              lastDirectionToggleAt = now;
              anchorY = currentY;
            }
          }
        } else {
          nextDirection = currentY > prevY ? 'down' : 'up';
          anchorY = currentY;
        }

        if (prevScrolled !== nextScrolled || prevDirection !== nextDirection) {
          const next: ScrollState = { isScrolled: nextScrolled, direction: nextDirection };
          prevScrolled = nextScrolled;
          prevDirection = nextDirection;
          setState(next);
          onScrollChangeRef.current?.(next);
        }

        prevY = currentY;
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(rafRef.current);
      if (scrollEndTimerRef.current !== null) {
        clearTimeout(scrollEndTimerRef.current);
        scrollEndTimerRef.current = null;
      }
      window.removeEventListener('scroll', handleScroll);
    };
  }, [threshold]);

  return { ...state, isScrolling };
};
